import { Injectable, NgZone } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { bufferTime, filter, map, mergeMap, observeOn } from 'rxjs/operators';
import { async } from 'rxjs/scheduler/async';
import {
  isRequestDataAction,
  RequestDataPayload,
  TypedAction,
} from './actions';
import { ApiHttp } from './api-http.service';
import { uniq } from './helpers';
import { mergeAll } from './operators';
import { referencesEqual } from './store.helpers';
import { Path } from './store.model';
import { enterZone, leaveZone } from './zone-util';

/**
 * Batch requests to group requests of the same endpoint with IDs to one
 * endpoint with a maximum of 1000 IDs.
 * @param requests The requests to batch.
 * @returns Batched requests.
 */
function batchRequests(
  requests: RequestDataPayload<any>[],
): RequestDataPayload<any>[] {
  const requestsToBatch: { [key: string]: RequestDataPayload<any> } = {};
  let finishedRequests: RequestDataPayload<any>[] = [];

  requests.forEach(request => {
    if (request.ref.query) {
      // We don't batch queries. Immediately put them in the finished pile.
      finishedRequests = [...finishedRequests, request];
    } else if (
      requestsToBatch[`${request.type}-${request.ref.path.authUid}`] ===
      undefined
    ) {
      requestsToBatch[`${request.type}-${request.ref.path.authUid}`] = request;
    } else {
      const path: Path<any> = {
        collection: request.ref.path.collection,
        authUid:
          requestsToBatch[`${request.type}-${request.ref.path.authUid}`].ref
            .path.authUid,
        ids: [
          ...(requestsToBatch[`${request.type}-${request.ref.path.authUid}`].ref
            .path.ids || []),
          ...(request.ref.path.ids || []),
        ],
      };
      const payload = { type: request.type, ref: { path } };
      // Limit batching of IDs to only 1000 IDs per request.
      if ((path.ids || []).length > 1000) {
        finishedRequests = [...finishedRequests, payload];
        delete requestsToBatch[`${request.type}-${request.ref.path.authUid}`];
      } else {
        requestsToBatch[
          `${request.type}-${request.ref.path.authUid}`
        ] = payload;
      }
    }
  });

  // No more requests to batch, combine the unbatchable requests with the rest of
  // the batchable requests for processing.
  return [...finishedRequests, ...Object.values(requestsToBatch)];
}

// TODO type refs/uniq back properly
@Injectable()
export class RequestDataEffect {
  @Effect()
  requestData: Observable<TypedAction> = this.actions.pipe(
    filter(isRequestDataAction),
    map(action => action.payload),
    bufferTime(250, leaveZone(this.ngZone, async)),
    filter(requests => requests.length > 0),
    observeOn(enterZone(this.ngZone, async)),
    map(requests => {
      const uniqRequests = uniq(requests, (a, b) =>
        referencesEqual(a.ref, b.ref),
      );
      return batchRequests(uniqRequests);
    }),
    mergeAll(),
    mergeMap(
      request => this.apiHttp.fetch(request.ref),
      (request, res) => ({ res, request }),
    ),
    map(({ res, request }) => {
      switch (res.status) {
        case 'success': {
          const action: TypedAction = {
            type: 'API_FETCH_SUCCESS',
            payload: {
              type: request.ref.path.collection,
              result: res.result,
              ref: request.ref,
            },
          };
          return action;
        }
        case 'error': {
          const action: TypedAction = {
            type: 'API_FETCH_ERROR',
            payload: {
              type: request.ref.path.collection,
              message: res.message,
              ref: request.ref,
            },
          };
          return action;
        }
        default: {
          const action: TypedAction = {
            type: 'UNKNOWN_API_FETCH_ERROR',
            payload: {
              type: request.ref.path.collection,
              message: res,
              ref: request.ref,
            },
          };
          return action;
        }
      }
    }),
  );

  constructor(
    private apiHttp: ApiHttp,
    private actions: Actions<TypedAction>,
    private ngZone: NgZone,
  ) {}
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/request-data.effect.ts